/*
  Aura Farmers product page JS. No dependencies.
  - <product-form-picker>: variant pills, price, button state, ?variant= URL, AJAX add to cart.
    Without JS the form posts to /cart/add normally (a noscript select picks the variant).
  - <product-recommendations>: loads related products through section rendering when scrolled near.
  Strings come from data attributes on the elements, set by the sections from locale files.
*/
(() => {
  'use strict';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('product-form-picker')) {
    class ProductFormPicker extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-product-form]');
        this.idInput = this.querySelector('[data-variant-id]');
        if (!this.form || !this.idInput) return;

        // The hidden id is disabled in the markup so the noscript select can win without JS
        this.idInput.disabled = false;

        const json = this.querySelector('script[data-product-json]');
        try {
          this.variants = JSON.parse(json.textContent).variants;
        } catch (error) {
          return;
        }

        this.fieldsets = Array.from(this.querySelectorAll('[data-option]'));
        this.buttons = Array.from(this.querySelectorAll('[data-add-button]'));
        this.priceEls = Array.from(this.querySelectorAll('[data-price]'));
        this.feedback = this.querySelector('[data-feedback]');
        this.qtyInput = this.querySelector('[data-qty-input]');
        this.galleryList = this.querySelector('[data-gallery-list]');
        this.galleryCount = this.querySelector('[data-gallery-count]');
        this.renderedId = this.idInput.value;
        this.busy = false;

        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        this.form.addEventListener('submit', this.onSubmit);
        window.addEventListener('pageshow', this.onPageShow);

        this.sync();
        this.initGalleryCount();
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.observer) this.observer.disconnect();
      }

      /* Events */

      onChange = (event) => {
        if (!event.target.matches('[data-option] input')) return;
        this.sync({ fromUser: true });
      };

      onClick = (event) => {
        // Open the size guide before the browser scrolls to it
        const guide = event.target.closest('[data-size-guide-link]');
        if (guide) {
          const target = document.getElementById(guide.getAttribute('href').slice(1));
          const details = target && target.closest('details');
          if (details) details.open = true;
          return;
        }

        const step = event.target.closest('[data-qty-step]');
        if (!step || !this.qtyInput) return;

        const current = parseInt(this.qtyInput.value, 10) || 1;
        this.qtyInput.value = Math.max(1, current + Number(step.dataset.qtyStep));
        this.clearFeedback();
      };

      // Browsers can restore radios from the back/forward cache, so trust the pills
      onPageShow = (event) => {
        if (event.persisted) this.sync();
      };

      onSubmit = (event) => {
        if (!window.fetch) return; // plain form post
        event.preventDefault();
        if (this.busy || !this.variant || !this.variant.available) return;
        this.add();
      };

      /* Variant state */

      selectedOptions() {
        if (!this.fieldsets.length) return null;
        const selected = this.fieldsets.map((fieldset) => {
          const input = fieldset.querySelector('input:checked');
          return input ? input.value : null;
        });
        return selected.includes(null) ? null : selected;
      }

      findVariant(options) {
        return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
      }

      sync({ fromUser = false } = {}) {
        const selected = this.selectedOptions();
        this.variant = selected
          ? this.findVariant(selected) || null
          : this.variants.find((variant) => String(variant.id) === this.idInput.value) || null;

        if (selected) {
          this.updateLegends();
          this.updateOptionStates(selected);
        }

        if (this.variant) {
          this.idInput.value = this.variant.id;
          if (String(this.variant.id) !== this.renderedId) {
            this.renderedId = String(this.variant.id);
            this.priceEls.forEach((el) => this.renderPrice(el));
            this.querySelectorAll('[data-sticky-variant]').forEach((el) => {
              el.textContent = this.variant.options.join(' / ');
            });
            this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.updateButtons();

        if (fromUser) {
          this.clearFeedback();
          this.updateUrl();
          this.showMedia();
        }
      }

      updateLegends() {
        this.fieldsets.forEach((fieldset) => {
          const input = fieldset.querySelector('input:checked');
          const label = fieldset.querySelector('[data-selected-value]');
          if (input && label) label.textContent = input.value;
        });
      }

      // For each pill: would picking it (with the other options as they are) land on a real, buyable variant?
      updateOptionStates(selected) {
        const text = { 'sold-out': this.dataset.soldOutText, unavailable: this.dataset.unavailableText };

        this.fieldsets.forEach((fieldset, index) => {
          fieldset.querySelectorAll('.pill').forEach((pill) => {
            const input = pill.querySelector('input');
            const candidate = selected.slice();
            candidate[index] = input.value;

            const match = this.findVariant(candidate);
            const state = !match ? 'unavailable' : match.available ? '' : 'sold-out';

            pill.dataset.state = state;
            input.disabled = state === 'unavailable' && !input.checked;
            const stateText = pill.querySelector('[data-state-text]');
            if (stateText) stateText.textContent = text[state] || '';
          });
        });
      }

      updateButtons() {
        const variant = this.variant;
        let label = this.dataset.addText;
        let disabled = false;

        if (!variant) {
          label = this.dataset.unavailableText;
          disabled = true;
        } else if (!variant.available) {
          label = this.dataset.soldOutText;
          disabled = true;
        }

        this.buttons.forEach((button) => {
          button.disabled = disabled;
          button.removeAttribute('aria-busy');
          const text = button.querySelector('[data-add-label]');
          if (text) text.textContent = label;
        });
      }

      renderPrice(el) {
        const variant = this.variant;
        const nodes = [];

        const span = (tag, className, text) => {
          const node = document.createElement(tag);
          if (className) node.className = className;
          node.textContent = text;
          return node;
        };

        if (variant.compare_at_price) {
          nodes.push(
            span('span', 'visually-hidden', this.dataset.salePriceText),
            span('span', '', variant.price),
            span('span', 'visually-hidden', this.dataset.regularPriceText),
            span('s', 'price__compare', variant.compare_at_price)
          );
        } else {
          nodes.push(span('span', '', variant.price));
        }

        el.replaceChildren(...nodes);
      }

      updateUrl() {
        if (this.dataset.updateUrl !== 'true' || !this.variant) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variant.id);
        window.history.replaceState({}, '', url);
      }

      /* Gallery */

      showMedia() {
        const id = this.variant && this.variant.media_id;
        const list = this.galleryList;
        if (!id || !list) return;

        const target = list.querySelector(`[data-media-id="${id}"]`);
        if (!target) return;

        const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
        if (list.scrollWidth > list.clientWidth) {
          list.scrollTo({ left: target.offsetLeft, behavior });
        } else {
          target.scrollIntoView({ block: 'nearest', behavior });
        }
      }

      initGalleryCount() {
        const list = this.galleryList;
        const count = this.galleryCount;
        if (!list || !count || !('IntersectionObserver' in window)) return;

        const items = Array.from(list.children);
        const template = count.dataset.template;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              count.textContent = template
                .replace('%current%', items.indexOf(entry.target) + 1)
                .replace('%total%', items.length);
            });
          },
          { root: list, threshold: 0.6 }
        );

        items.forEach((item) => this.observer.observe(item));
      }

      /* Add to cart */

      setBusy() {
        this.busy = true;
        this.buttons.forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          const text = button.querySelector('[data-add-label]');
          if (text) text.textContent = this.dataset.addingText;
        });
      }

      async add() {
        this.setBusy();
        this.clearFeedback();

        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          const data = await response.json();

          if (!response.ok) {
            this.showError(data.description || data.message);
          } else {
            document.dispatchEvent(new CustomEvent('cart:updated'));
            this.showAdded();
          }
        } catch (error) {
          this.showError();
        } finally {
          this.busy = false;
          this.updateButtons();
        }
      }

      showAdded() {
        if (!this.feedback) return;
        const link = document.createElement('a');
        link.className = 'pdp-form__added';
        link.href = this.dataset.cartUrl;
        link.textContent = this.dataset.addedText;
        this.feedback.replaceChildren(link);
      }

      showError(message) {
        if (!this.feedback) return;
        const text = document.createElement('p');
        text.className = 'form-message';
        text.textContent = message || this.dataset.errorText;
        this.feedback.replaceChildren(text);
      }

      clearFeedback() {
        if (this.feedback) this.feedback.replaceChildren();
      }
    }

    customElements.define('product-form-picker', ProductFormPicker);
  }

  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url || this.dataset.loaded) return;

        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '300px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        this.dataset.loaded = 'true';

        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) return;

          const html = await response.text();
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) this.replaceChildren(...fresh.childNodes);
        } catch (error) {
          // Leave the section empty. Related products are not worth an error message.
        }
      }
    }

    customElements.define('product-recommendations', ProductRecommendations);
  }
})();
